import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage, LANGUAGE_LABELS } from "@/lib/i18n";
import { Menu, X, ChevronDown, Globe } from "lucide-react";

export const Navbar = () => {
  const { t, language, changeLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setProductsOpen(false);
    setCustomOpen(false);
    setLangOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";
  const navBg = scrolled || !isHome
    ? "bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm"
    : "bg-transparent";
  const textColor = scrolled || !isHome ? "text-slate-800" : "text-white";
  const logoColor = scrolled || !isHome ? "text-slate-800" : "text-white";

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/engineering", label: t("nav.engineering") },
    { to: "/blog", label: t("nav.blog") },
  ];

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${navBg}`}>
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20 lg:h-24 overflow-visible">
          {/* Logo - with overflow clipped to prevent click interference */}
          <Link to="/" data-testid="logo-link" className="flex items-center -my-2 md:-my-8 overflow-hidden" style={{ maxHeight: '96px' }}>
            <img src="https://customer-assets.emergentagent.com/job_pruva-wellness/artifacts/vnrhurmy_PRUVA%20%283%29.svg" alt="Pruva Jacuzzi" className="h-24 sm:h-28 md:h-48 lg:h-56 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} data-testid={`nav-${link.to.slice(1) || 'home'}`}
                className={`text-sm uppercase tracking-widest font-medium hover:opacity-70 transition-opacity duration-200 ${textColor}`}>
                {link.label}
              </Link>
            ))}

            {/* Products Dropdown */}
            <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <button data-testid="nav-products-dropdown"
                className={`text-sm uppercase tracking-widest font-medium hover:opacity-70 transition-opacity duration-200 flex items-center gap-1 py-2 ${textColor}`}>
                {t("nav.products")} <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {productsOpen && (
                <div className="absolute top-full left-0 pt-2 w-52" >
                <div className="bg-white shadow-xl border border-slate-100 animate-fade-in">
                  <Link to="/spa-series" data-testid="nav-spa-series" className="block px-5 py-3 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-800 transition-colors duration-200">
                    {t("nav.spaSeries")}
                  </Link>
                  <Link to="/jacuzzi-series" data-testid="nav-jacuzzi-series" className="block px-5 py-3 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-800 transition-colors duration-200">
                    {t("nav.jacuzziSeries")}
                  </Link>
                </div>
                </div>
              )}
            </div>

            {/* Custom Solutions Dropdown */}
            <div className="relative" onMouseEnter={() => setCustomOpen(true)} onMouseLeave={() => setCustomOpen(false)}>
              <button data-testid="nav-custom-dropdown"
                className={`text-sm uppercase tracking-widest font-medium hover:opacity-70 transition-opacity duration-200 flex items-center gap-1 py-2 ${textColor}`}>
                {t("nav.customSolutions")} <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {customOpen && (
                <div className="absolute top-full left-0 pt-2 w-56">
                  <div className="bg-white shadow-xl border border-slate-100 animate-fade-in">
                    <Link to="/custom-solutions" data-testid="nav-custom-solutions" className="block px-5 py-3 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-800 transition-colors duration-200">
                      {t("nav.customSolutions")}
                    </Link>
                    <Link to="/custom-solutions/developer-services" data-testid="nav-developer-services" className="block px-5 py-3 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-800 transition-colors duration-200">
                      {t("nav.developerServices")}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" data-testid="nav-contact"
              className={`text-sm uppercase tracking-widest font-medium hover:opacity-70 transition-opacity duration-200 ${textColor}`}>
              {t("nav.contact")}
            </Link>

            {/* Language Switcher */}
            <div className="relative">
              <button data-testid="language-switcher" onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity duration-200 ${textColor}`}>
                <Globe className="w-4 h-4" />
                <span className="uppercase">{language}</span>
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white shadow-xl border border-slate-100 animate-fade-in">
                  {languages.map(lang => (
                    <button key={lang} data-testid={`lang-${lang}`} onClick={() => { changeLanguage(lang); setLangOpen(false); }}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 ${lang === language ? 'bg-slate-100 text-slate-800 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}>
                      {LANGUAGE_LABELS[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button data-testid="mobile-menu-toggle" onClick={() => setIsOpen(!isOpen)} className={`lg:hidden ${textColor}`}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-100 shadow-lg animate-fade-in">
          <div className="container mx-auto px-4 py-4 space-y-1">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} className="block py-3 text-sm uppercase tracking-widest text-slate-700 font-medium hover:text-slate-800">
                {link.label}
              </Link>
            ))}
            <Link to="/spa-series" className="block py-3 text-sm uppercase tracking-widest text-slate-700 font-medium hover:text-slate-800 ps-4 border-s-2 border-slate-300">
              {t("nav.spaSeries")}
            </Link>
            <Link to="/jacuzzi-series" className="block py-3 text-sm uppercase tracking-widest text-slate-700 font-medium hover:text-slate-800 ps-4 border-s-2 border-slate-300">
              {t("nav.jacuzziSeries")}
            </Link>
            <Link to="/custom-solutions" className="block py-3 text-sm uppercase tracking-widest text-slate-700 font-medium hover:text-slate-800">
              {t("nav.customSolutions")}
            </Link>
            <Link to="/custom-solutions/developer-services" className="block py-3 text-sm uppercase tracking-widest text-slate-700 font-medium hover:text-slate-800 ps-4 border-s-2 border-slate-300">
              {t("nav.developerServices")}
            </Link>
            <Link to="/contact" className="block py-3 text-sm uppercase tracking-widest text-slate-700 font-medium hover:text-slate-800">
              {t("nav.contact")}
            </Link>
            <div className="pt-3 border-t border-slate-100 flex gap-2">
              {languages.map(lang => (
                <button key={lang} data-testid={`mobile-lang-${lang}`} onClick={() => { changeLanguage(lang); setIsOpen(false); }}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider font-medium transition-colors duration-200 ${lang === language ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
